import React, { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Download, Loader2 } from "lucide-react";

const SAMPLE = "\uFEFFName,Phone,Gender\nTwana aziz,07501112233,male\nLana Omar,07709998877,female\n";

const downloadSample = () => {
  const url = URL.createObjectURL(new Blob([SAMPLE], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url; a.download = "students-sample.csv"; a.click();
  URL.revokeObjectURL(url);
};

export default function ImportStudentsDialog({ group, onOpenChange, onDone }) {
  const { t } = useLang();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const ref = useRef();

  const close = () => { setFile(null); setError(""); onOpenChange(false); };

  const upload = async () => {
    if (!file || !group) return;
    setBusy(true); setError("");
    const fd = new FormData();
    fd.append("group_id", group.id);
    fd.append("file", file);
    try {
      const { data } = await api.post("/students/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("importStudents.done").replace("{created}", data.created).replace("{skipped}", data.skipped));
      close(); onDone();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!group} onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="import-students-dialog">
        <DialogHeader><DialogTitle>{t("importStudents.title")}</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="text-muted-foreground">{t("importStudents.group")}: <span className="font-medium text-foreground" data-testid="import-group-name">{group?.name}</span></div>
          <p className="text-muted-foreground">{t("importStudents.help")}</p>
          <button type="button" onClick={downloadSample} data-testid="import-sample-btn" className="inline-flex items-center gap-2 text-sm font-medium" style={{ color: "var(--brand)" }}>
            <Download className="h-4 w-4" /> {t("importStudents.template")}
          </button>
          <input ref={ref} type="file" accept=".xlsx,.csv" data-testid="import-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full rounded-lg border border-border bg-card px-3 py-2 text-sm file:me-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-1" />
          {error && <div className="rounded-lg bg-destructive/10 px-3 py-2 text-destructive" data-testid="import-error">{error}</div>}
        </div>
        <DialogFooter>
          <button type="button" onClick={close} className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-secondary" data-testid="import-cancel">{t("importStudents.cancel")}</button>
          <button type="button" onClick={upload} disabled={!file || busy} data-testid="import-submit" className="brand-btn inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {busy ? t("importStudents.uploading") : t("importStudents.upload")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
